import {
  UserCheck,
  LayoutDashboard,
  BarChart3,
  QrCode,
  UserPlus,
  Settings,
  ClipboardList,
  Shield,
  Monitor,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

/**
 * Sidebar navigation for Check-Ins page
 * Follows the same design pattern as TasksSidebar for consistency
 */
export const CheckInsSidebar = ({
  totalOnSite = 0,
  showAnalytics = false,
  onToggleAnalytics,
  showTimesheets = false,
  onToggleTimesheets,
  showLiveCompliance = false,
  onToggleLiveCompliance,
  complianceGapCount = 0,
  showKiosks = false,
  onToggleKiosks,
  kioskCount = 0,
  onShowQRCode,
  onOpenManualCheckIn,
  isStackView = false,
  showSettings = false,
  onToggleSettings,
  showDashboard = true,
  onShowDashboard,
}) => {

  return (
    <nav className="flex flex-col h-full bg-background border-r">
      {/* Navigation Groups */}
      <div className="flex-1 overflow-y-auto p-3 space-y-6">
        {/* Main Navigation */}
        <div>
          <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Navigation
          </h3>
          <div className="space-y-1">
            {/* Dashboard */}
            <button
              onClick={onShowDashboard}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                showDashboard && !showAnalytics && !showSettings && !showTimesheets && !showKiosks
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <LayoutDashboard className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 text-left">Dashboard</span>
              {totalOnSite > 0 && (
                <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-300">
                  {totalOnSite}
                </Badge>
              )}
            </button>

            {/* Live Compliance */}
            <button
              onClick={onToggleLiveCompliance}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                showLiveCompliance
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <Shield className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 text-left">Live Compliance</span>
              {complianceGapCount > 0 && (
                <Badge variant="destructive" className="text-xs px-1.5 py-0 h-4">
                  {complianceGapCount}
                </Badge>
              )}
            </button>

            {/* Timesheets */}
            <button
              onClick={onToggleTimesheets}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                showTimesheets
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <ClipboardList className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 text-left">Timesheets</span>
            </button>

            {/* Analytics View */}
            <button
              onClick={onToggleAnalytics}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                showAnalytics
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <BarChart3 className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 text-left">Analytics</span>
            </button>

            {/* Kiosks - Only show if not stack view */}
            {!isStackView && onToggleKiosks && (
              <button
                onClick={onToggleKiosks}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  showKiosks
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Monitor className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 text-left">Kiosks</span>
                {kioskCount > 0 && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-300">
                    {kioskCount}
                  </Badge>
                )}
              </button>
            )}

            {/* Settings - Only show if not stack view */}
            {!isStackView && onToggleSettings && (
              <button
                onClick={onToggleSettings}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  showSettings
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Settings className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 text-left">Settings</span>
              </button>
            )}
          </div>
        </div>

        {/* Actions Group */}
        {!isStackView && (
          <div>
            <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Actions
            </h3>
            <div className="space-y-1">
              {/* QR Code */}
              <button
                onClick={onShowQRCode}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <QrCode className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 text-left">QR Code</span>
              </button>

              {/* Manual Check-In */}
              <button
                onClick={onOpenManualCheckIn}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <UserPlus className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 text-left">Manual Check-In</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer - On-Site Summary */}
      <div className="p-3 border-t bg-muted/30">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
            <UserCheck className="h-4 w-4 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">Currently On-Site</p>
            <p className="text-lg font-bold text-foreground">{totalOnSite}</p>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default CheckInsSidebar;
